/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */

/* URL d'ENTRÉE capturée au tout début, AVANT que VariantSelector.updateURL ne réécrive ?color=
   via replaceState. Permet à mm_product_media.js de savoir si l'utilisateur est arrivé avec une
   couleur forcée (lien partagé / retour panier) → la galerie se filtre alors dès l'arrivée. */
window.MM_ENTRY_SEARCH = window.MM_ENTRY_SEARCH ?? window.location.search;

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
// Produits > 250 variants : Shopify plafonne product.variants (SSR) et les endpoints
// .js/.json à 250. On complète via la section paginée MM-Product-Variants-Page (250/page).
const VARIANTS_PAGE_SIZE = 250;
const VARIANTS_PAGE_SECTION = 'mm-product-variants-page';

const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,


	// Selectors
	selectors: {
		variantSelector: 'variant-selector',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */

// Product Event Emitter
const productEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'product'
	};

	return document.dispatchEvent(new CustomEvent(`product:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache for global elements
const ProductDOM = {
	get classicCTA() { return document.querySelector('[data-cta]'); },
	get priceElements() { return document.querySelectorAll('[data-price]'); },
	get ctaPriceElements() { return document.querySelectorAll('[data-cta-price]'); },
	get ctaComparePriceElements() { return document.querySelectorAll('[data-cta-compare-price]'); }
};

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;

		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			window.log('info', 'Product', 'Variant ID updated', {
				oldVariantId: oldValue,
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			productEmit('variant-input-updated', { variantId });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		const newElements = html.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);

		productEmit('product-reload-start', { elementsCount: elementsToUpdate.length });

		let updatedCount = 0;

		elementsToUpdate.forEach((currentElement, index) => {
			// Try to match by ID first, then by specific class, fallback to index
			let newElement = null;

			// Une classe spécifique reconnue → matching ciblé (jamais de fallback index,
			// sinon un élément conditionnel qui apparaît/disparaît — ex: mm-pdp-livraison
			// absent en rupture — décale les index et injecte le mauvais HTML, ce qui
			// faisait apparaître le CTA back-in-stock dans le label couleur).
			let specificClass = null;
			if (currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			} else if (currentElement.className) {
				const classList = Array.from(currentElement.classList);
				specificClass = classList.find(cls =>
					cls.includes('component-price') ||
					cls.includes('component-quantity') ||
					cls.includes('component-cta') ||
					cls.includes('sticky-cta') ||
					cls.includes('pdp-livraison')
				);

				if (specificClass) {
					const candidates = html.querySelectorAll(`.${specificClass}[data-product-reload]`);
					newElement = candidates[0] || null;
				}
			}

			// Fallback index uniquement pour les éléments sans id ni classe spécifique.
			// Un élément à classe spécifique introuvable dans le nouveau HTML est laissé
			// en place (pas de fallback index qui désalignerait).
			if (!newElement && !currentElement.id && !specificClass) {
				newElement = newElements[index];
			}

			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					currentElement.outerHTML = newElement.outerHTML;
					updatedCount++;
					productEmit('product-reload-element-updated', {
						elementIndex: index,
						matchType: currentElement.id ? 'id' : (currentElement.className.includes('component') ? 'class' : 'index')
					});
				} catch (error) {
					window.log('warn', 'Product', 'Element update failed', {
						index,
						elementId: currentElement.id || 'no-id',
						elementTag: currentElement.tagName,
						elementClasses: currentElement.className,
						error: error.message
					});
				}
			}
		});

		window.log('info', 'Product', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount,
			skippedElements: elementsToUpdate.length - updatedCount,
			elementTypes: [...new Set(Array.from(elementsToUpdate).map(el => el.tagName.toLowerCase()))]
		});

		productEmit('product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	shouldUpdateElement(currentElement, newElement) {
		const currentContent = currentElement.innerHTML?.trim();
		const newContent = newElement.innerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 VARIANT SELECTION LOGIC
 */
class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;

		this.container = this.closest(PRODUCT_CONFIG.selectors.productForm) || this.closest('form');

		this.onVariantChange = this.onVariantChange.bind(this);

		// Produits > 250 variants : le SSR (product.variants) et #product-variants-json
		// sont plafonnés à 250 → les couleurs au-delà sont absentes. On complète d'abord
		// les données (fetch des pages 2+), PUIS on init. En dessous de 250 : init direct.
		const variantsCount = parseInt(this.dataset.variantsCount, 10) || 0;
		if (variantsCount > VARIANTS_PAGE_SIZE) {
			this._completeVariantsThenInit(variantsCount);
		} else {
			this.init();
		}
	}

	/**
	 * Complète les variants/couleurs manquants (index > 250) en fetchant la section
	 * paginée MM-Product-Variants-Page (page par page), merge dans #product-variants-json
	 * et .mm-pcard__colors-data, PUIS lance init(). Non bloquant pour le LCP (le SSR est
	 * déjà rendu) ; init() est simplement retardé du temps du fetch pour ce type de produit.
	 */
	async _completeVariantsThenInit(variantsCount) {
		try {
			await this._fetchMissingVariants(variantsCount);
		} catch (error) {
			window.log('warn', 'Product', 'Variant completion failed, init with SSR data only', { error: error.message });
		}
		this.init();
		// Le compteur "voir les X couleurs" du ColorPanel s'est initialisé sur le JSON
		// tronqué à 250 (DOMContentLoaded, avant la fin du fetch) : le recaler sur le complet.
		window.ColorPanelManager?._refreshSeeAllCount?.();
	}

	/**
	 * Fetch les pages 2..N de la section paginée et merge variants + colors dans le DOM.
	 */
	async _fetchMissingVariants(variantsCount) {
		const handle = this.dataset.handle;
		if (!handle) return;
		const totalPages = Math.ceil(variantsCount / VARIANTS_PAGE_SIZE);

		const pages = [];
		for (let p = 2; p <= totalPages; p++) pages.push(p);
		const results = await Promise.all(pages.map(p => this._fetchVariantsPage(handle, p)));

		const newVariants = [];
		const newColors = [];
		const seenColorNames = new Set();
		const newMediaIds = {};
		const newMediaData = {};
		const newInfoModele = {};
		for (const data of results) {
			if (!data) continue;
			if (Array.isArray(data.variants)) newVariants.push(...data.variants);
				if (Array.isArray(data.colors)) {
				// Une couleur à cheval sur plusieurs pages de 250 est émise une fois par page
				// (dédup Liquid réinitialisée à chaque page) : dédup inter-pages par nom ici.
				for (const c of data.colors) {
					if (seenColorNames.has(c.name)) continue;
					seenColorNames.add(c.name);
					newColors.push(c);
				}
			}
			if (data.mediaIds) Object.assign(newMediaIds, data.mediaIds);
			if (data.mediaData) Object.assign(newMediaData, data.mediaData);
			if (data.infoModele) Object.assign(newInfoModele, data.infoModele);
		}
		if (newVariants.length) this._mergeVariantsJSON(newVariants);
		if (newColors.length) this._mergeColorsJSON(newColors);
		this._mergeMediaMaps(newMediaIds, newMediaData, newInfoModele);
		// Données complètes : updateOptionsAvailability injecte dès lors les couleurs manquantes.
		this._variantsCompleted = true;
	}

	/**
	 * Complète les maps média du DOM (lues par mm_product_media.js à chaque usage) avec les
	 * entrées des variants > 250 : #variant-media-ids, #variant-media-data (selon le mode du
	 * template) et #variant-info-modele.variants. Ne crée aucun élément : le mode médias est
	 * décidé par le SSR, on ne fait que compléter les maps existantes.
	 */
	_mergeMediaMaps(mediaIds, mediaData, infoModele) {
		const mergeInto = (selector, entries) => {
			if (!entries || !Object.keys(entries).length) return;
			const el = document.querySelector(selector);
			if (!el) return;
			const map = window.parseJSON(el, {});
			for (const [id, entry] of Object.entries(entries)) {
				if (!(id in map)) map[id] = entry;
			}
			el.textContent = JSON.stringify(map);
		};
		mergeInto('#variant-media-ids', mediaIds);
		mergeInto('#variant-media-data', mediaData);

		if (infoModele && Object.keys(infoModele).length) {
			const el = document.querySelector('#variant-info-modele');
			if (el) {
				const map = window.parseJSON(el, { default: '', variants: {} });
				map.variants = map.variants || {};
				for (const [id, html] of Object.entries(infoModele)) {
					if (!(id in map.variants)) map.variants[id] = html;
				}
				el.textContent = JSON.stringify(map);
				// getVariantInfoModele est mémoïsé côté MediaManager : invalider la mémo.
				if (window.MediaManager) window.MediaManager._infoModeleMap = undefined;
			}
		}
	}

	/**
	 * Fetch une page de la section paginée via Section Rendering API, parse le JSON embarqué.
	 * @returns {{variants:Array, colors:Array}|null}
	 */
	async _fetchVariantsPage(handle, page) {
		const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
		const url = `${root}products/${handle}?section_id=${VARIANTS_PAGE_SECTION}&page=${page}&_v=${window.mmAssetsV || ''}`;
		try {
			const res = await fetch(url);
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const html = await res.text();
			const doc = new DOMParser().parseFromString(html, 'text/html');
			const el = doc.querySelector('.mm-variants-page-data');
			return el ? window.parseJSON(el, null) : null;
		} catch (error) {
			window.log('warn', 'Product', `Failed to fetch variants page ${page}`, { error: error.message });
			return null;
		}
	}

	/**
	 * Concatène les variants fetchés (dédup par id) dans #product-variants-json.
	 * variantData n'est pas encore mémoïsé à ce stade (init() pas lancé) → on réécrit le DOM.
	 */
	_mergeVariantsJSON(newVariants) {
		const el = this.querySelector('#product-variants-json');
		if (!el) return;
		const existing = window.parseJSON(el, []);
		const seen = new Set(existing.map(v => v.id));
		const merged = existing.concat(newVariants.filter(v => !seen.has(v.id)));
		el.textContent = JSON.stringify(merged);
	}

	/**
	 * Concatène les couleurs fetchées (dédup par name) dans .mm-pcard__colors-data (JSON panel),
	 * SANS retrier : le SSR rend déjà toutes les couleurs dans l'ordre (product_option_value),
	 * l'ordre du JSON SSR fait foi et la rangée ne bouge pas une fois la page affichée. Les
	 * couleurs absentes du SSR (sans variant résolu) s'ajoutent en fin de liste.
	 * Ce JSON alimente _panelColorData → _injectMissingSwatches / _toggleSeeAllColors / tri de
	 * la rangée. Crée l'élément s'il n'existe pas encore.
	 */
	_mergeColorsJSON(newColors) {
		let el = this.querySelector('.mm-pcard__colors-data');
		const existing = el ? window.parseJSON(el, []) : [];
		const seen = new Set(existing.map(c => c.name));
		const merged = existing.concat(newColors.filter(c => !seen.has(c.name)));

		if (!el) {
			el = document.createElement('script');
			el.type = 'application/json';
			el.className = 'mm-pcard__colors-data';
			this.appendChild(el);
		}
		el.textContent = JSON.stringify(merged);
	}

	init() {
		try {
			this.addEventListener("change", this.onVariantChange);

			// Clic utilisateur sur une pastille couleur : marque l'interaction (à partir de là
			// la galerie se filtre sur la couleur ; avant, elle montre toutes les images produit).
			// Si la pastille est DÉJÀ cochée, le `change` natif ne se déclenche pas → on filtre
			// ici. Un clic sur une AUTRE pastille passe par `change` → onVariantChange (le flag
			// est déjà posé ci-dessous, donc le filtrage s'applique).
			this.addEventListener('click', (e) => {
				const label = e.target.closest('.mm-pdp-swatch-item label');
				if (!label) return;
				if (window.MediaManager) window.MediaManager.userSelectedColor = true;
				const input = label.closest('.mm-pdp-swatch-item')?.querySelector('input[data-variant-input]');
				if (input?.checked && this.currentVariant) this.updateMediaDisplay();
			});

			// Tooltip nom couleur : recentrer s'il déborde du viewport (pastilles en bord de
			// ligne sur mobile). On corrige --tt-shift à l'ouverture (hover/focus).
			this.addEventListener('pointerenter', this._positionSwatchTooltip, true);
			this.addEventListener('focusin', this._positionSwatchTooltip);

			// Sélection par défaut = variant natif Shopify (SSR). ?color=<slug> peut surcharger
			// la couleur ; ?variant= est géré par le SSR, sauf quand sa couleur n'a pas de radio.
			const urlParams = new URLSearchParams(window.location.search);
			const urlForcesColor = urlParams.has('color') || urlParams.has('variant');
			this.applyUrlColorSelection();

			this.getSelectedOptions();
			this.getSelectedVariant();
			this.updateSelectedOptionLabels();
			this.updateCheckedVisualState();
			this.updateOptionsAvailability();

			if (this.currentVariant) {
				this.updateCTAButtonPrice();
				this.updateURL();
				this.updateProductData();
				// mm_product_media.js s'initialise AVANT ce custom element : il a filtré la
				// galerie et écrit l'info modèle sur data-selected-variant-id encore au variant
				// SSR. Quand l'URL force une couleur, le variant résolu ici peut différer →
				// rejouer le pipeline média (images + info modèle) sur le bon variant.
				if (urlForcesColor) this.updateMediaDisplay();
			}

			// ?color=<slug> ciblant une couleur sans radio SSR (sans pastille / au-delà de 250) :
			// appliquer maintenant que le pipeline est prêt (injecte la pastille + sélectionne).
			if (this._pendingUrlColor) {
				const c = this._pendingUrlColor;
				this._pendingUrlColor = null;
				this.selectColorByValue(c.name, c.style);
			}

			if (this.container) {
				this.container.addEventListener('submit', () => {
					this.getSelectedOptions();
					this.getSelectedVariant();
					if (this.currentVariant) {
						ProductManager.updateVariantId(this.container, this.currentVariant.id);
					}
				});
			}
		} catch (error) {
			console.error('[VariantSelector] init failed:', error.message);
		}
	}

	/**
	 * Slugifie une valeur de couleur (ex: "marine foncé" → "marine-fonce").
	 * Source unique de vérité pour le round-trip ?color= (écrit par updateURL,
	 * relu par selectColorFromUrlSlug).
	 */
	_slugifyColor(value) {
		return value
			.toLowerCase()
			.normalize('NFD').replace(/[̀-ͯ]/g, '')
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '');
	}

	/**
	 * Recentre le tooltip de la pastille couleur pour qu'il ne déborde pas du viewport.
	 * Le tooltip est centré sur la pastille (translateX -50%) ; pour les pastilles en
	 * bord de ligne (mobile), le corps sort de l'écran → --tt-shift le ramène dans une
	 * marge de 8px. La flèche (::after) reste centrée sur la pastille.
	 */
	_positionSwatchTooltip(e) {
		const label = e.target.closest?.('.mm-pdp-swatch__label[title]');
		if (!label) return;
		label.style.removeProperty('--tt-shift');
		// Le ::before est un pseudo non mesurable : on approxime sa demi-largeur via un span
		// caché aux mêmes métriques, puis on borne les bords du tooltip au viewport.
		requestAnimationFrame(() => {
			const dot = label.getBoundingClientRect();
			const center = dot.left + dot.width / 2;
			const MARGIN = 8;
			const vw = document.documentElement.clientWidth;
			const half = this._measureTooltipHalfWidth(label.getAttribute('title'));
			let shift = 0;
			const leftEdge = center - half;
			const rightEdge = center + half;
			if (leftEdge < MARGIN) shift = MARGIN - leftEdge;
			else if (rightEdge > vw - MARGIN) shift = (vw - MARGIN) - rightEdge;
			if (shift) label.style.setProperty('--tt-shift', `${Math.round(shift)}px`);
		});
	}

	/** Mesure la demi-largeur du tooltip pour un texte donné (span caché aux mêmes métriques que le ::before). */
	_measureTooltipHalfWidth(text) {
		let m = this._ttMeasureEl;
		if (!m) {
			m = document.createElement('span');
			m.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;' +
				'text-transform:capitalize;font-size:12px;font-weight:400;letter-spacing:0.5px;' +
				'padding:6px 10px;left:-9999px;top:-9999px;';
			m.style.fontFamily = getComputedStyle(this).getPropertyValue('--font-body-family') || 'inherit';
			document.body.appendChild(m);
			this._ttMeasureEl = m;
		}
		m.textContent = text || '';
		return m.getBoundingClientRect().width / 2;
	}

	/**
	 * ?color= est un param maison (SEO-friendly) que Liquid IGNORE :
	 * selected_or_first_available_variant ne réagit qu'à ?variant=<id>. Le SSR rend
	 * donc le variant natif Shopify (pastille + galerie), pas la couleur du slug.
	 * On lit ?color= à l'init et on coche la pastille correspondante (match sur valeur
	 * slugifiée) AVANT que mm_product_media.js ne filtre la galerie sur
	 * data-selected-variant-id. Le reste du pipeline (getSelectedVariant → media) suit.
	 * @returns {boolean} true si une pastille a été (re)cochée depuis le slug.
	 */
	selectColorFromUrlSlug() {
		const params = new URLSearchParams(window.location.search);
		const slug = params.get('color');
		if (!slug) return false;

		const colorInputs = this.querySelectorAll('input[data-variant-input][data-option-position="1"]');
		const target = Array.from(colorInputs).find(input => this._slugifyColor(input.value) === slug);
		if (target) {
			if (target.checked) return true; // déjà cochée → ok
			target.checked = true; // décoche automatiquement l'ancien radio (même name)
			return true;
		}

		// Aucun radio SSR pour ce slug : couleur sans pastille (à la commande, voire au-delà de 250,
		// injectée par le fetch paginé). La retrouver dans le panel JSON et mémoriser pour
		// l'appliquer via selectColorByValue APRÈS l'init du pipeline (radio absent = flux hors-liste).
		const colors = this._panelColorData();
		if (Array.isArray(colors)) {
			const match = colors.find(c => this._slugifyColor(c.name) === slug);
			if (match) {
				this._pendingUrlColor = match;
				return true;
			}
		}
		return false; // introuvable → no-op
	}

	/**
	 * Au chargement : on respecte le variant natif Shopify (selected_or_first_available_variant,
	 * déjà coché côté SSR) comme sélection par défaut. Seul un ?color=<slug> dans l'URL peut
	 * surcharger la couleur (lien maison écrit par updateURL) ; ?variant= est géré nativement
	 * PAR LE SSR — sauf quand sa couleur n'a aucun radio (voir selectColorFromUrlVariant).
	 */
	applyUrlColorSelection() {
		const params = new URLSearchParams(window.location.search);
		if (params.has('variant')) {
			this.selectColorFromUrlVariant(params.get('variant'));
			return;
		}
		if (params.has('color')) this.selectColorFromUrlSlug();
	}

	/**
	 * Filet pour ?variant=<id> ciblant une couleur SANS radio SSR.
	 *
	 * Le picker ne rend que les couleurs achetables non "à la commande" (voir
	 * _swatchColorData) : un ?variant= pointant une couleur onDemand — ou au-delà
	 * des 250 premiers variants — laissait la PDP SANS aucune couleur cochée (seule la taille l'était),
	 * car ce chemin sortait dès qu'un ?variant= était présent en supposant le SSR
	 * autoritaire. On rattrape ici via le slot [data-out-of-list], comme le flux ?color=.
	 *
	 * No-op dans le cas nominal : si le SSR a déjà coché la couleur du variant, on sort.
	 * @returns {boolean} true si une couleur hors-liste a été mémorisée.
	 */
	selectColorFromUrlVariant(variantId) {
		const id = parseInt(variantId, 10);
		if (!Number.isInteger(id)) return false;

		const variant = this.getVariantJSON().find(v => v.id === id);
		if (!variant || !Array.isArray(variant.options)) return false;

		// Position de l'option couleur via une pastille existante (jamais de comparaison
		// sur le nom d'option : il est traduit — "Couleur"/"Colour").
		const swatchInput = this.querySelector('.mm-pdp-swatch-item input[data-variant-input]');
		const colorPosition = parseInt(swatchInput?.dataset.optionPosition, 10) || 1;
		const colorName = variant.options[colorPosition - 1];
		if (!colorName) return false;

		// Le SSR a-t-il un radio pour cette couleur ? (nominal → il l'a déjà coché)
		const hasRadio = Array.from(
			this.querySelectorAll(`input[data-variant-input][data-option-position="${colorPosition}"]`)
		).some(inp => inp.value === colorName);
		if (hasRadio) return false;

		// Radio absent : retrouver la couleur dans le JSON panel (il contient TOUTES les
		// couleurs, onDemand incluses) et l'appliquer après l'init du pipeline.
		const colors = this._panelColorData();
		if (!Array.isArray(colors)) return false;
		const match = colors.find(c => c.name === colorName);
		if (!match) return false;

		this._pendingUrlColor = match;
		return true;
	}

	onVariantChange(event) {
		setTimeout(() => {
			try {
				if (this.hasAllOptionsSelected()) this.toggleSizeError(false);
				this.getSelectedOptions();
				// Si une pastille couleur native est cochée, retirer la pastille dynamique hors-liste.
				const colorChecked = this.querySelector('.mm-pdp-swatch-item input[data-variant-input]:checked');
				if (colorChecked) this._removeOutOfListSwatch();
				this.updateSelectedOptionLabels();
				this.updateCheckedVisualState();
				// Disponibilité d'abord : un changement de couleur peut désélectionner la taille.
				this.updateOptionsAvailability();
				this.getSelectedVariant();

				if (this.currentVariant) {
					this.updateCTAButtonPrice();
					this.updateURL();
					this.updateProductData();
					this.updateMediaDisplay();
				}
			} catch (error) {
				console.error('[VariantSelector] onVariantChange error:', error.message);
			}
		}, 10);
	}

	updateCTAButtonPrice() {
		if (!this.currentVariant || typeof this.currentVariant.price === 'undefined') return;

		const quantityInput = document.querySelector('input[data-qtty-input]');
		const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
		const formattedPrice = window.formatMoney(this.currentVariant.price * quantity);

		ProductDOM.ctaPriceElements.forEach((priceElement) => {
			priceElement.dataset.price = this.currentVariant.price;
			priceElement.textContent = formattedPrice;
		});

		const compareAt = this.currentVariant.compare_at_price;
		const priceGroup = document.querySelector('.mm-cta-price-group');
		if (!priceGroup) return;

		let compareEl = priceGroup.querySelector('[data-cta-compare-price]');

		if (compareAt && compareAt > this.currentVariant.price) {
			const formattedCompare = window.formatMoney(compareAt);

			if (!compareEl) {
				compareEl = document.createElement('span');
				compareEl.className = 'mm-cta-compare-price';
				compareEl.dataset.ctaComparePrice = '';
				priceGroup.insertBefore(compareEl, priceGroup.firstChild);
			}
			compareEl.dataset.comparePrice = compareAt;
			compareEl.textContent = formattedCompare;
		} else if (compareEl) {
			compareEl.remove();
		}

		// Remise gérée à la couleur : badge -XX% (+ libellé "Promotion" s'il n'y a pas de tag
		// produit) affiché selon le variant courant ; la note, au même coin, est masquée en remise.
		const hasDiscount = compareAt && compareAt > this.currentVariant.price;
		const discountPct = hasDiscount ? Math.floor((compareAt - this.currentVariant.price) * 100 / compareAt) : 0;
		document.querySelectorAll('[data-discount-badge]').forEach(el => {
			if (hasDiscount) el.textContent = `-${discountPct}%`;
		});
		document.querySelectorAll('.mm-media-promotion-label, .mm-media-discount-badge').forEach(el => {
			el.style.display = hasDiscount ? '' : 'none';
		});
		document.querySelectorAll('.mm-media-rating-overlay').forEach(el => {
			el.style.display = hasDiscount ? 'none' : '';
		});
	}

	updateCheckedVisualState() {
		this.querySelectorAll('input[data-variant-input]').forEach(input => {
			const label = input.nextElementSibling;
			if (!label) return;
			label.classList.toggle('is-selected', input.checked);
		});
	}

	/**
	 * Keep option labels in sync with checked inputs (color/size).
	 */
	updateSelectedOptionLabels() {
		const selectedLabels = this.querySelectorAll('[data-option-selected-value]');
		if (!selectedLabels.length) return;

		selectedLabels.forEach((label) => {
			const position = parseInt(label.dataset.optionSelectedValue, 10);
			if (!position) return;

			const selectedInput = this.querySelector(
				`input[data-variant-input][data-option-position="${position}"]:checked, select[data-variant-input][data-option-position="${position}"]`
			);

			// Sans input coché : couleur hors-liste (portée par this.options) ou taille non
			// choisie / désélectionnée → libellé vidé (le séparateur "·" disparaît en CSS).
			label.textContent = selectedInput?.value || (this.options || [])[position - 1] || '';
		});
	}

	/**
	 * Update product data
	 */
	async updateProductData() {
		// Update variant ID immediately
		ProductManager.updateVariantId(this.container, this.currentVariant.id);
		this.updateDeliveryBadge();

		// Fetch and update product data
		const html = await this.fetchVariantData();
		if (html) {
			ProductManager.updateReloadElements(html);
			this.updateVariantOptions(html);
			this.updateDeliveryProperty(html);
		}
	}

	/**
	 * Resynchronise la line item property "Délai" (envoyée au checkout) sur la variante courante.
	 * Source de vérité : l'input [data-delivery-property] rendu par le SSR pour cette variante
	 * (name + value = même règle stock/livraison que la PDP). En rupture sèche, le SSR n'émet pas
	 * de `name` → on retire aussi le name des inputs live pour ne pas soumettre la property.
	 */
	updateDeliveryProperty(html) {
		const source = html.querySelector('[data-delivery-property]');
		if (!source) return;

		const value = source.getAttribute('value') || '';
		const name = source.getAttribute('name'); // null en rupture sèche

		document.querySelectorAll('[data-delivery-property]').forEach((input) => {
			input.value = value;
			if (name) {
				input.setAttribute('name', name);
			} else {
				input.removeAttribute('name');
			}
		});
	}

	/**
	 * Update media display
	 */
	updateMediaDisplay() {
		// Use MediaManager from the separate media module
		if (window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(this.currentVariant);
		} else {
			window.log('warn', 'Product', 'MediaManager or updateImagesVariant method not available', {
				mediaManagerExists: !!window.MediaManager,
				hasUpdateMethod: !!window.MediaManager?.updateImagesVariant
			});
		}
	}

	/**
	 * Fetch variant data
	 */
	async fetchVariantData() {
		if (!this.currentVariant?.id) return null;

		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;

		if (!sectionId) {
			window.log('warn', 'Product', 'VariantSelector: No section ID provided');
			return null;
		}

		const variantUrl = `${baseUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}&_v=${window.mmAssetsV || ''}`;

		// Check cache first
		if (this.cached.has(variantUrl)) {
			return this.cached.get(variantUrl);
		}

		try {
			const response = await fetch(variantUrl);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const responseText = await response.text();
			const html = new DOMParser().parseFromString(responseText, 'text/html');

			// Cache the result
			this.cached.set(variantUrl, html);

			productEmit('variant-fetch-success', { variantId: this.currentVariant.id });
			return html;
		} catch (error) {
			window.log('error', 'Product', 'Failed to fetch variant data', { error: error.message });
			return null;
		}
	}

	/**
	 * Get selected options indexed by data-option-position — reads attribute directly on input,
	 * with fallback to closest ancestor for backwards compatibility.
	 */
	getSelectedOptions() {
		const byPosition = {};
		// Couleur hors-liste (panel) : portée par le slot [data-out-of-list], pas par un radio coché.
		// La seeder d'abord pour ne pas perdre l'axe couleur ; un input coché la surchargerait.
		const outOfList = this.querySelector('.mm-pdp-swatch-item[data-out-of-list]');
		if (outOfList?.dataset.colorValue) {
			const pos = parseInt(outOfList.dataset.optionPosition, 10);
			if (pos) byPosition[pos] = outOfList.dataset.colorValue;
		}
		this.querySelectorAll('input[data-variant-input]:checked, select[data-variant-input]').forEach(el => {
			const pos = parseInt(el.dataset.optionPosition, 10)
				|| parseInt(el.closest('[data-option-position]')?.dataset.optionPosition, 10);
			if (!pos || !el.value) return;
			byPosition[pos] = el.value;
		});
		// Tableau indexé par position (options[pos - 1]) : une option non choisie (taille à
		// l'arrivée sur la fiche) laisse un trou au lieu de décaler les suivantes. every/forEach
		// ignorent les trous → getSelectedVariant matche sur les seules options choisies.
		this.options = [];
		Object.keys(byPosition).forEach(pos => { this.options[pos - 1] = byPosition[pos]; });
	}

	/**
	 * Get the selected variant based on options.
	 */
	getSelectedVariant() {
		const variants = this.getVariantJSON();

		if (!variants.length) {
			this.currentVariant = null;
			return;
		}

		// Sans taille choisie, plusieurs variants matchent (toutes les tailles de la couleur) :
		// on prend le 1er disponible comme variant représentatif (prix, délai, médias, URL).
		// L'ajout panier reste bloqué tant que la taille n'est pas choisie (hasAllOptionsSelected).
		const matches = variants.filter(variant => {
			return this.options.every((option, index) => {
				const value = variant.options && variant.options.length
					? variant.options[index]
					: variant[`option${index + 1}`];
				return value === option;
			});
		});
		this.currentVariant = matches.find(v => v.available) || matches[0] || null;
	}

	/**
	 * Badge délai (.mm-pdp-livraison) : même règle que le Liquid de MM-Main-Product.
	 * - Taille choisie → délai exact de la variante.
	 * - Sinon → MEILLEUR délai parmi toutes les tailles de la couleur sélectionnée.
	 * Basé sur le STOCK réel (on vend hors stock : tout est `available`). Priorité :
	 * 24/48h (stock > 0, ou sans suivi d'inventaire) > 4/5 sem. (continue selling)
	 * > fabrication spéciale (continue + onDemand) > rupture sèche.
	 * Le span n'est pas en data-product-reload : le re-fetch serveur ne l'écrase pas.
	 */
	updateDeliveryBadge() {
		const badge = this.querySelector('[data-delivery-badge]');
		if (!badge) return;

		const swatchInput = this.querySelector('.mm-pdp-swatch-item input[data-variant-input]');
		const colorPos = parseInt(swatchInput?.dataset.optionPosition, 10) || 1;
		const color = (this.options || [])[colorPos - 1];
		const optAt = (v, pos) => (v.options && v.options.length ? v.options[pos - 1] : v[`option${pos}`]);

		const pool = this.currentVariant && this.hasAllOptionsSelected()
			? [this.currentVariant]
			: this.getVariantJSON().filter(v => optAt(v, colorPos) === color);
		if (!pool.length) return;

		// Sans suivi d'inventaire : available avec policy deny et stock 0 → expédiable de suite
		const inStock = v => (v.inventory_quantity || 0) > 0 || (v.available && v.policy !== 'continue');
		const continueSelling = v => !inStock(v) && v.policy === 'continue';

		let state = '';
		if (pool.some(inStock)) state = 'fast';
		else if (pool.some(v => continueSelling(v) && !v.onDemand)) state = 'restock';
		else if (pool.some(v => continueSelling(v) && v.onDemand)) state = 'on-demand';

		const label = {
			'fast': badge.dataset.labelFast,
			'restock': badge.dataset.labelRestock,
			'on-demand': badge.dataset.labelOnDemand,
			'': badge.dataset.labelOut
		}[state] || '';

		const nodes = [];
		if (state) {
			const dot = document.createElement('span');
			dot.className = `mm-pdp-livraison__dot mm-pdp-livraison__dot--${state}`;
			dot.setAttribute('aria-hidden', 'true');
			nodes.push(dot);
		}
		nodes.push(document.createTextNode(label));
		badge.replaceChildren(...nodes);
	}

	/**
	 * Toutes les options hors couleur (taille…) ont-elles été choisies ? Elles ne sont plus
	 * présélectionnées à l'arrivée sur la fiche : tant que ce n'est pas le cas, l'ajout panier
	 * est bloqué (SizeGate). Groupes concernés = ceux qui portent le message [data-size-error].
	 */
	hasAllOptionsSelected() {
		return [...this.querySelectorAll('.mm-pdp-option-group')].every(group =>
			!group.querySelector('[data-size-error]') || !!group.querySelector('input[data-variant-input]:checked')
		);
	}

	/** Affiche (ou masque) le message "Veuillez sélectionner une taille" sur les groupes non choisis. */
	toggleSizeError(show) {
		let firstMissing = null;
		this.querySelectorAll('.mm-pdp-option-group').forEach(group => {
			const error = group.querySelector('[data-size-error]');
			if (!error) return;
			const missing = show && !group.querySelector('input[data-variant-input]:checked');
			error.hidden = !missing;
			group.classList.toggle('mm-pdp-option-group--error', missing);
			if (missing && !firstMissing) firstMissing = group;
		});
		return firstMissing;
	}

	/**
	 * Sélectionne une couleur par sa valeur, depuis l'extérieur (ex: drawer "voir toutes les couleurs").
	 * Reproduit le résultat d'un clic sur une pastille du bloc, que la couleur soit dans les 14
	 * pastilles rendues (radio présent → coche + change natif) ou au-delà (pas de radio → pipeline direct).
	 * @param {string} colorName - valeur de l'option couleur (= input value / nom couleur)
	 * @param {string} [swatchStyle] - style CSS du dot (pour injecter une pastille si couleur hors-liste)
	 */
	selectColorByValue(colorName, swatchStyle = '') {
		if (!colorName) return;

		// Sélection via le panel "voir toutes les couleurs" = choix explicite : la galerie doit
		// se filtrer sur la couleur (comme un clic pastille), y compris afficher le message
		// "pas d'aperçu" si la couleur n'a aucune image.
		if (window.MediaManager) window.MediaManager.userSelectedColor = true;

		// Position de l'option couleur via une pastille existante.
		const swatchInput = this.querySelector('.mm-pdp-swatch-item input[data-variant-input]');
		const colorPosition = parseInt(swatchInput?.dataset.optionPosition, 10) || 1;

		// Cas pastille : un radio existe → réutiliser le flux natif (coche + change).
		const radio = [...this.querySelectorAll('input[data-variant-input]')]
			.find(r => parseInt(r.dataset.optionPosition, 10) === colorPosition && r.value === colorName);
		if (radio) {
			this._removeOutOfListSwatch(); // couleur avec pastille → retirer la pastille dynamique
			if (!radio.checked) {
				radio.checked = true;
				radio.dispatchEvent(new Event('change', { bubbles: true }));
			}
			return;
		}

		// Cas hors-liste : pas de radio. Mettre à jour l'option couleur et rejouer le pipeline.
		this.getSelectedOptions();
		this.options[colorPosition - 1] = colorName;

		const variants = this.getVariantJSON();
		const optAt = (v, pos) => (v.options && v.options.length ? v.options[pos - 1] : v[`option${pos}`]);
		// Variant représentatif de la couleur (achetable en priorité) : porte la pastille hors-liste.
		const sameColor = variants.filter(v => optAt(v, colorPosition) === colorName);
		const target = sameColor.find(v => v.available) || sameColor[0];
		if (!target) return;

		// Refléter la couleur hors-liste dans le bloc : injecter une pastille active dédiée.
		this._setOutOfListSwatch(colorName, swatchStyle, colorPosition, target.id);

		// Comme le flux natif (onVariantChange) : grise les tailles indispo du coloris et
		// désélectionne la taille cochée si elle n'existe pas dans cette couleur (ticket 86capy9ec).
		this.updateOptionsAvailability();
		// Variant résolu sur la sélection réelle (slot hors-liste + taille éventuelle).
		this.getSelectedOptions();
		this.getSelectedVariant();
		if (!this.currentVariant) return;

		this.updateCTAButtonPrice();
		this.updateURL(colorName);
		this.updateProductData();
		this.updateMediaDisplay();
	}

	/**
	 * Affiche dans le bloc des pastilles la couleur sélectionnée hors-liste (sans radio natif).
	 * Réutilise un slot unique [data-out-of-list] : désélectionne les autres, l'insère en tête, l'active.
	 */
	_setOutOfListSwatch(colorName, swatchStyle, colorPosition, variantId) {
		const container = this.querySelector('.mm-pdp-swatches');
		if (!container) return;

		// Désélectionner les pastilles natives (input + label).
		this.querySelectorAll('.mm-pdp-swatch-item input[data-variant-input]').forEach(inp => {
			if (parseInt(inp.dataset.optionPosition, 10) !== colorPosition) return;
			inp.checked = false;
			inp.nextElementSibling?.classList.remove('is-selected');
		});

		// Réutiliser ou créer le slot dynamique.
		let item = container.querySelector('.mm-pdp-swatch-item[data-out-of-list]');
		if (!item) {
			item = document.createElement('div');
			item.className = 'mm-pdp-swatch-item';
			item.dataset.outOfList = '';
			// PAS de data-variant-value : éviter de décaler l'index dans updateVariantOptions (re-fetch).
			container.insertBefore(item, container.firstChild);
		}
		const safeName = colorName.replace(/"/g, '&quot;');
		item.innerHTML =
			`<span class="mm-pdp-swatch__label is-selected mm-cursor-pointer" title="${safeName}">` +
			`<span class="mm-pdp-swatch__dot" style="${swatchStyle || ''}"></span></span>`;
		item.dataset.variantId = variantId || '';
		// Couleur/position portées par le slot (pas de radio natif) : getSelectedOptions les relit
		// pour ne pas perdre l'axe couleur hors-liste quand la taille est recalculée.
		item.dataset.colorValue = colorName;
		item.dataset.optionPosition = colorPosition;

		// Mettre à jour le label "NOM couleur" (sinon il reste sur l'ancienne couleur native).
		const colorLabel = this.querySelector(`[data-option-selected-value="${colorPosition}"]`);
		if (colorLabel) colorLabel.textContent = colorName;
	}

	/** Retire la pastille dynamique hors-liste (couleur avec pastille native sélectionnée). */
	_removeOutOfListSwatch() {
		this.querySelector('.mm-pdp-swatches .mm-pdp-swatch-item[data-out-of-list]')?.remove();
	}

	/**
	 * Get variant data from embedded JSON
	 */
	getVariantJSON() {
		if (!this.variantData) {
			const jsonElement = this.querySelector('#product-variants-json') || this.querySelector('[type="application/json"]');

			if (!jsonElement) {
				window.log('error', 'Product', 'VariantSelector: No JSON data element found');
				return this.variantData = [];
			}

			try {
				const jsonText = jsonElement.textContent.trim();
				this.variantData = jsonText ? JSON.parse(jsonText) : [];
			} catch (error) {
				window.log('error', 'Product', 'Failed to parse variant JSON', { error: error.message });
				this.variantData = [];
			}
		}
		return this.variantData;
	}

	/**
	 * Update URL with selected color slug (SEO-friendly, no variant ID).
	 * Format: /products/handle?color=nom-couleur
	 */
	updateURL(colorValueOverride = null) {
		if (!this.currentVariant?.id) return;

		try {
			const baseUrl = this.dataset.url || window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);

			// Couleur explicite (ex: sélection hors-liste depuis le drawer, sans radio coché) sinon radio coché.
			// L'option couleur est repérée par la position du bloc pastilles (option.name est traduit
			// par le storefront — "Couleur"/"Colour" — ne jamais matcher le nom FR en dur).
			const swatchInput = this.querySelector('.mm-pdp-swatch-item input[data-variant-input]');
			const colorPos = swatchInput?.dataset.optionPosition;
			const checkedColor = colorPos
				? this.querySelector(`input[data-variant-input][data-option-position="${colorPos}"]:checked`)?.value
				: null;
			// Couleur hors-liste (slot sans radio) : la relire pour garder ?color= même quand le
			// pipeline est rejoué sans override.
			const outOfListColor = this.querySelector('.mm-pdp-swatch-item[data-out-of-list]')?.dataset.colorValue;
			const colorValue = colorValueOverride || checkedColor || outOfListColor;
			if (colorValue) {
				const colorSlug = this._slugifyColor(colorValue);
				url.searchParams.delete('variant');
				url.searchParams.set('color', colorSlug);
			} else if (this.hasAllOptionsSelected()) {
				url.searchParams.set('variant', this.currentVariant.id);
			} else {
				// Taille pas encore choisie : currentVariant n'est qu'un représentatif → pas de ?variant=
				url.searchParams.delete('variant');
			}

			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			window.log('error', 'Product', 'Failed to update URL', { error: error.message });
		}
	}

	/**
	 * Update variant options without breaking event listeners
	 */
	updateVariantOptions(html) {
		// Retirer les pastilles injectées AVANT l'appariement par index : elles portent
		// data-variant-value et fausseraient l'alignement currentOptions↔newOptions (le HTML
		// re-fetché ne contient que les pastilles SSR). updateOptionsAvailability les recalcule ensuite.
		this.querySelectorAll('[data-injected]').forEach(el => el.remove());

		const currentOptions = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		const newOptions = html.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);

		if (!newOptions.length) return;

		// Appariement par (position d'option, valeur) et non par index : l'ordre de la rangée peut
		// différer du HTML re-fetché (tri JS, pastilles injectées, HTML servi par un autre thème),
		// un décalage d'index recopiait l'état d'une autre couleur — voire d'une taille.
		const optionKey = input => `${input.dataset.optionPosition}|${input.value}`;
		const newInputs = new Map();
		newOptions.forEach(newOption => {
			const newInput = newOption.querySelector('input[data-variant-input]');
			if (newInput) newInputs.set(optionKey(newInput), newInput);
		});

		currentOptions.forEach(currentOption => {
			const currentInput = currentOption.querySelector('input[data-variant-input]');
			const newInput = currentInput && newInputs.get(optionKey(currentInput));
			// Seul l'état disabled est repris ; classes et checked restent ceux du DOM courant
			// (la visibilité est recalculée juste après par updateOptionsAvailability).
			if (newInput) currentInput.disabled = newInput.disabled;
		});

		// Le re-fetch Shopify pose --oos/disabled sur dispo GLOBALE. On recalcule le croisé couleur×taille.
		this.updateOptionsAvailability();
	}

	/**
	 * Recalcule pastilles couleur et tailles sur le JSON variants embarqué.
	 * - Couleurs (.mm-pdp-swatch-item) : visibles si au moins une taille est expédiable sous
	 *   24/48h (stock physique, ou sans suivi de stock). JAMAIS croisé avec la taille choisie :
	 *   la rangée ne change pas quand on sélectionne une taille. La couleur cochée reste visible.
	 * - Tailles (.mm-pdp-size-item) : disabled si aucun variant achetable pour la couleur choisie.
	 *   Si la taille cochée n'existe pas dans la nouvelle couleur, elle est désélectionnée.
	 */
	updateOptionsAvailability() {
		const swatchItems = this.querySelectorAll('.mm-pdp-swatch-item');
		const sizeItems = this.querySelectorAll('.mm-pdp-size-item');
		if (!swatchItems.length && !sizeItems.length) return;

		const variants = this.getVariantJSON();
		if (!variants.length) return;

		const optAt = (v, pos) => (v.options && v.options.length ? v.options[pos - 1] : v[`option${pos}`]);

		const colorPosition = parseInt(
			this.querySelector('.mm-pdp-swatch-item input[data-variant-input]')?.dataset.optionPosition, 10
		) || 1;

		// Sélection courante par position. Seed depuis this.options : une couleur hors-liste (panel)
		// n'a pas de radio coché mais this.options la porte. Les inputs cochés affinent.
		const selectedByPos = {};
		(this.options || []).forEach((val, i) => { if (val) selectedByPos[i + 1] = val; });
		this.querySelectorAll('input[data-variant-input]:checked, select[data-variant-input]').forEach(el => {
			const pos = parseInt(el.dataset.optionPosition, 10);
			if (pos && el.value) selectedByPos[pos] = el.value;
		});
		const selectedColor = selectedByPos[colorPosition];
		// Couleur cochée à l'arrivée sur la page (1er appel = init) : reste visible même si elle
		// n'est pas expédiable sous 24/48h, sinon elle disparaît dès qu'on choisit une autre
		// couleur et la rangée vue au chargement change.
		if (this._initialColor === undefined) this._initialColor = selectedColor ?? null;

		// Taille achetable pour la couleur (et les autres options) choisies.
		const isAvailable = (position, value) => variants.some(v => {
			if (!v.available || optAt(v, position) !== value) return false;
			return Object.keys(selectedByPos).every(p => p == position || optAt(v, p) === selectedByPos[p]);
		});

		// Couleur achetable (toutes tailles) : sert au bouton "voir toutes les couleurs".
		const isColorPurchasable = (position, value) =>
			variants.some(v => v.available && optAt(v, position) === value);

		// Couleur avec pastille : au moins une taille expédiable sous 24/48h (policy continue sans
		// stock = réassort 3/4 semaines → panel uniquement). Même règle que le Liquid (instock_map).
		// Fin de série (data-end-of-series) : toute couleur achetable a sa pastille.
		const endOfSeries = this.hasAttribute('data-end-of-series');
		const isColorShown = (position, value) => value === selectedColor || value === this._initialColor || variants.some(v =>
			v.available && optAt(v, position) === value && (endOfSeries || v.inventory_quantity > 0 || v.policy !== 'continue')
		);

		let sizeCleared = false;
		sizeItems.forEach(item => {
			const input = item.querySelector('input[data-variant-input]');
			if (!input) return;
			const available = isAvailable(parseInt(input.dataset.optionPosition, 10), input.value);
			input.disabled = !available;
			if (input.checked && !available) {
				input.checked = false;
				sizeCleared = true;
			}
		});
		if (sizeCleared) {
			this.getSelectedOptions();
			this.updateCheckedVisualState();
			this.updateSelectedOptionLabels();
		}

		// Retirer les pastilles injectées au tour précédent : on recalcule le delta à neuf.
		// Sélecteur sur [data-injected] seul (pas couplé à la classe) : un re-fetch a pu
		// écraser leur className, elles doivent quand même être nettoyées.
		this.querySelectorAll('[data-injected]').forEach(el => el.remove());

		const ssrSwatchItems = this.querySelectorAll('.mm-pdp-swatch-item');
		ssrSwatchItems.forEach(item => {
			const input = item.querySelector('input[data-variant-input]');
			if (!input) return;
			const shown = isColorShown(parseInt(input.dataset.optionPosition, 10), input.value);
			item.classList.toggle('mm-pdp-swatch-item--oos', !shown);
		});

		// Produits > 250 variants : couleurs sans variant résolu au SSR → injectées depuis le JSON panel.
		this._injectMissingSwatches(ssrSwatchItems, isColorShown);

		// Ordre visuel de la rangée = ordre du JSON panel (= ordre SSR par teinte).
		this._reorderSwatchRow();

		// Le bouton "voir toutes les couleurs" ne s'affiche que s'il reste des couleurs
		// achetables sans pastille visible (réassort, à la commande).
		this._toggleSeeAllColors(isColorPurchasable);
	}

	/**
	 * Affiche/masque le bouton "voir toutes les couleurs" : visible seulement s'il existe
	 * au moins une couleur achetable (pour la taille courante) qui n'a pas de pastille visible
	 * dans le bloc. Sinon le panel n'apporterait rien de plus que les pastilles déjà montrées.
	 */
	_toggleSeeAllColors(isAvailable) {
		const trigger = this.querySelector('.mm-pcard__see-all-colors');
		if (!trigger) return;

		const colors = this._panelColorData();
		const container = this.querySelector('.mm-pdp-swatches');
		const refInput = container?.querySelector('.mm-pdp-swatch-item input[data-variant-input]');
		const position = parseInt(refInput?.dataset.optionPosition, 10);
		if (!Array.isArray(colors) || !Number.isInteger(position)) return;

		// Couleurs achetables affichées (pastilles visibles).
		const shown = new Set();
		container.querySelectorAll('.mm-pdp-swatch-item:not(.mm-pdp-swatch-item--oos)').forEach(it => {
			const v = it.querySelector('input[data-variant-input]')?.value;
			if (v != null) shown.add(v);
		});

		// Reste-t-il une couleur achetable (pour la taille) non affichée ?
		const hasMore = colors.some(c => isAvailable(position, c.name) && !shown.has(c.name));
		trigger.hidden = !hasMore;
	}

	/**
	 * Réordonne la rangée de pastilles selon l'ordre du panel (stock global décroissant) : SSR,
	 * injectées et hors-liste restent toujours dans le même ordre. Déplace les nœuds existants
	 * (appendChild) — états et listeners conservés. Sans JSON panel, l'ordre SSR fait foi.
	 * _panelColorData (pas _swatchColorData) : une couleur à la commande choisie via le panel
	 * (pastille hors-liste) prend aussi sa place dans le tri.
	 */
	_reorderSwatchRow() {
		const colors = this._panelColorData();
		if (!Array.isArray(colors) || !colors.length) return;
		const container = this.querySelector('.mm-pdp-swatches');
		if (!container) return;

		const rank = new Map(colors.map((c, i) => [c.name, i]));
		const items = [...container.querySelectorAll('.mm-pdp-swatch-item')];
		items
			.map(item => {
				const value = item.querySelector('input[data-variant-input]')?.value
					|| item.querySelector('[title]')?.getAttribute('title');
				return { item, rank: rank.has(value) ? rank.get(value) : Number.MAX_SAFE_INTEGER };
			})
			.sort((a, b) => a.rank - b.rank)
			.forEach(({ item }) => container.appendChild(item));
	}

	/**
	 * Affiche toutes les couleurs ACHETABLES : le SSR rend chaque couleur des 250 premiers
	 * variants ; on injecte depuis le JSON panel (mêmes données triées, complétées au-delà de
	 * 250) les couleurs dispo non déjà rendues.
	 */
	_injectMissingSwatches(ssrSwatchItems, isAvailable) {
		const colors = this._swatchColorData();
		if (!Array.isArray(colors) || !colors.length) return;

		const container = this.querySelector('.mm-pdp-swatches');
		if (!container) return;

		// Couleur = position du 1er input couleur rendu (data-option-position).
		const refInput = container.querySelector('.mm-pdp-swatch-item input[data-variant-input]');
		const position = parseInt(refInput?.dataset.optionPosition, 10);
		if (!Number.isInteger(position)) return;
		const optionName = (refInput.name.match(/options\[(.+)\]/) || [])[1] || 'Couleur';

		// Couleurs déjà rendues (SSR + slot hors-liste sélectionné), par valeur — anti-doublon.
		const rendered = new Set();
		ssrSwatchItems.forEach(item => {
			const v = item.querySelector('input[data-variant-input]')?.value;
			if (v != null) rendered.add(v);
		});
		// La pastille hors-liste (couleur choisie via le panel, sans radio natif) compte aussi :
		// ne pas réinjecter une pastille pour la même couleur.
		const outOfList = container.querySelector('.mm-pdp-swatch-item[data-out-of-list]');
		if (outOfList) rendered.add(outOfList.querySelector('[title]')?.getAttribute('title'));

		// Couleur actuellement sélectionnée : si elle est réinjectée (couleur hors-liste choisie),
		// la pastille doit rester cochée — sinon le recalcul efface l'état et updateURL retombe
		// sur ?variant= au lieu de ?color=.
		const selectedColor = Array.isArray(this.options) ? this.options[position - 1] : null;

		// Parcourir les couleurs triées : injecter celles dispo (pour la taille) non rendues.
		const frag = document.createDocumentFragment();
		for (let i = 0; i < colors.length; i++) {
			const c = colors[i];
			if (rendered.has(c.name)) continue;
			if (!isAvailable(position, c.name)) continue;
			const item = this._buildSwatchItem(c, optionName, position, i);
			if (selectedColor != null && c.name === selectedColor) {
				item.querySelector('input').checked = true;
				item.querySelector('.mm-pdp-swatch__label')?.classList.add('is-selected');
			}
			frag.appendChild(item);
			rendered.add(c.name);
		}
		if (frag.childNodes.length) container.appendChild(frag);
	}

	/**
	 * Couleurs (ordre de tri) issues du JSON panel embarqué (.mm-pcard__colors-data),
	 * présent s'il y a > 14 couleurs ou des couleurs à la commande (complété au-delà de 250
	 * variants). Sert au tri de la rangée et à l'injection des couleurs absentes du SSR.
	 * Mémoïsé. Format : [{name, style, mediaId, id}, ...].
	 */
	_panelColorData() {
		if (this._panelColors !== undefined) return this._panelColors;
		const el = this.querySelector('.mm-pcard__colors-data');
		this._panelColors = window.parseJSON(el, null);
		return this._panelColors;
	}

	/**
	 * Couleurs pour le RENDU DES PASTILLES : _panelColorData sans les couleurs "à la commande"
	 * (onDemand). Ces dernières restent dans _panelColorData (color panel + sélection explicite
	 * via selectColorByValue), mais ne doivent jamais apparaître en pastille dans la rangée.
	 */
	_swatchColorData() {
		const colors = this._panelColorData();
		return Array.isArray(colors) ? colors.filter(c => !c.onDemand) : colors;
	}

	/**
	 * Construit un .mm-pdp-swatch-item équivalent au SSR pour une couleur du JSON panel.
	 * Marqué [data-injected] pour être recalculé/retiré à chaque updateOptionsAvailability.
	 */
	_buildSwatchItem(color, optionName, position, index) {
		const item = document.createElement('div');
		item.className = 'mm-pdp-swatch-item';
		// PAS de data-variant-value : éviter de décaler l'appariement par index dans
		// updateVariantOptions (re-fetch), comme la pastille hors-liste (_setOutOfListSwatch).
		item.dataset.injected = '';

		const safeName = String(color.name);
		const id = `option-injected-${position}-${index}`;
		const input = document.createElement('input');
		input.type = 'radio';
		input.id = id;
		input.name = `options[${optionName}]`;
		input.value = safeName;
		input.dataset.variantInput = '';
		input.dataset.optionPosition = String(position);
		if (color.mediaId) input.dataset.swatchMediaId = String(color.mediaId);
		input.setAttribute('aria-label', safeName);

		const label = document.createElement('label');
		label.setAttribute('for', id);
		label.className = 'mm-pdp-swatch__label mm-cursor-pointer';
		label.title = safeName;
		const dot = document.createElement('span');
		dot.className = 'mm-pdp-swatch__dot';
		if (color.style) dot.style.cssText = color.style;
		label.appendChild(dot);

		item.append(input, label);
		return item;
	}
}

/*********************************************************************************************
 * 🏷️ UTILITY FUNCTIONS
 */

/**
 * Change quantity value and update prices
 */
function mmChangeQtty(delta, inputId) {
	const input = document.getElementById(inputId);
	if (!input) return;

	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
	const minValue = parseInt(input.getAttribute('min'), 10) || 1;

	// Calculate new value with constraints
	const newValue = Math.max(minValue, Math.min(maxValue, currentValue + parseInt(delta, 10)));

	input.value = newValue;
	input.dispatchEvent(new Event('change', { bubbles: true }));

	// Update prices
	mmUpdatePrice(newValue);

	// Update button states
	updateQuantityButtonStates(input);
}

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

	// Find the container
	const container = input.closest('.mm-qtty-container');
	if (!container) return;

	// Find plus button
	const plusBtn = container.querySelector('[data-qtty-increase]');

	// Update plus button only
	if (plusBtn) {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	}
}

/**
 * Update displayed prices based on quantity
 */
function mmUpdatePrice(qtty) {
	const quantity = parseInt(qtty, 10);
	if (!quantity || quantity < 1) return;

	productEmit('quantity-update-start', { quantity });

	try {
		let updatedElementsCount = 0;

		// Update price elements
		ProductDOM.priceElements.forEach(priceElement => {
			const priceValue = parseFloat(priceElement.dataset.price);
			if (!isNaN(priceValue)) {
				priceElement.textContent = window.formatMoney(priceValue * quantity);
				updatedElementsCount++;
			}
		});

		productEmit('quantity-update-success', {
			quantity,
			updatedElements: updatedElementsCount
		});

		window.log('info', 'Product', 'Price update completed', {
			quantity,
			updatedPriceElements: updatedElementsCount
		});
	} catch (error) {
		window.log('error', 'Product', 'Failed to update prices', {
			quantity,
			error: error.message
		});
		productEmit('quantity-update-error', {
			quantity,
			error: error.message
		});
	}
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Define custom element
customElements.define(PRODUCT_CONFIG.selectors.variantSelector, VariantSelector);

/*********************************************************************************************
 * 🚀 INITIALIZATION & ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	productEmit('initializing');

	try {
		productEmit('initialized');
		window.log('info', 'Product', 'Product page initialized successfully');
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize product page', { error: error.message });
	}

	// Initialize button states on page load
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		updateQuantityButtonStates(input);
	});

	// Use event delegation for quantity buttons (works even after DOM updates)
	// Product page buttons use data-qtty-decrease/increase
	// Cart buttons use data-cart-decrease/increase (handled by mm_cart.js)
	document.addEventListener('click', (e) => {
		// Handle decrease buttons
		const decreaseBtn = e.target.closest('[data-qtty-decrease]');
		if (decreaseBtn) {
			const inputId = decreaseBtn.dataset.qttyDecrease;
			window.mmChangeQtty(-1, inputId);
			return;
		}

		// Handle increase buttons
		const increaseBtn = e.target.closest('[data-qtty-increase]');
		if (increaseBtn) {
			const inputId = increaseBtn.dataset.qttyIncrease;
			window.mmChangeQtty(1, inputId);
			return;
		}
	});

	// Swatch hover: preview the full variant media (same as click) without selecting
	const _findVariantForSwatchValue = (swatchValue) => {
		const variantSelector = document.querySelector('variant-selector');
		if (!variantSelector) return null;
		const variants = window.parseJSON('#product-variants-json', []);
		// Get current options, replace option1 (color) with hovered swatch value
		const currentOptions = Array.from(
			variantSelector.querySelectorAll('input[data-variant-input]:checked, select[data-variant-input]')
		).sort((a, b) => {
			const posA = parseInt(a.dataset.optionPosition, 10);
			const posB = parseInt(b.dataset.optionPosition, 10);
			return posA - posB;
		}).map(el => el.value);

		if (!currentOptions.length) return null;
		// Replace the color option (position 1) with the hovered value
		const targetOptions = [swatchValue, ...currentOptions.slice(1)];

		return variants.find(v =>
			targetOptions.every((opt, i) => (v.options?.[i] ?? v[`option${i + 1}`]) === opt)
		) || variants.find(v => (v.options?.[0] ?? v.option1) === swatchValue) || null;
	};

	const _lockMediaHeight = () => {
		const stack = document.querySelector('[data-main-slider]');
			if (!stack) return;
		const currentLock = parseFloat(stack.style.minHeight) || 0;
		stack.style.minHeight = Math.max(stack.offsetHeight, currentLock) + 'px';
	};
	const _unlockMediaHeight = () => {
		const stack = document.querySelector('[data-main-slider]');
		if (stack) stack.style.minHeight = '';
	};

	// Toggle sur TOUS les messages no-preview (desktop + mobile) : seul celui du
	// conteneur média visible au viewport courant s'affiche (l'autre est display:none).
	const _toggleNoPreview = (show) => {
		document.querySelectorAll('.mm-pdp-no-preview').forEach(el => el.classList.toggle('mm-pdp-no-preview--visible', show));
	};

	document.addEventListener('mouseover', (e) => {
		if (window.isMobile()) return;
		const swatchItem = e.target.closest('.mm-pdp-swatch-item');
		if (!swatchItem) return;
		const input = swatchItem.querySelector('input[data-variant-input]');
		if (!input) return;

		// Pas d'image associée à cette couleur → message, pas de changement d'image
		if (!input.dataset.swatchMediaId) {
			_toggleNoPreview(true);
			return;
		}
		_toggleNoPreview(false);

		_lockMediaHeight();
		const variant = _findVariantForSwatchValue(input.value);
		if (variant) window.MediaManager?.updateImagesVariant(variant);
		
		requestAnimationFrame(_lockMediaHeight);
	});

	document.addEventListener('mouseout', (e) => {
		if (window.isMobile()) return;
		const swatchItem = e.target.closest('.mm-pdp-swatch-item');
		if (!swatchItem) return;
		if (swatchItem.contains(e.relatedTarget)) return;
		_toggleNoPreview(false);
		// Restore the currently selected variant's media
		const variantSelector = document.querySelector('variant-selector');
		if (variantSelector?.currentVariant) {
			window.MediaManager?.updateImagesVariant(variantSelector.currentVariant);
		}
		_unlockMediaHeight();
	});

	// Adjust quantity when product reload completes
	document.addEventListener('product:product-reload-complete', () => {
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			const currentValue = parseInt(input.value, 10) || 1;
			const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

			if (currentValue > maxValue) {
				input.value = maxValue;
			}

			updateQuantityButtonStates(input);
		});

		if (inputs.length > 0) {
			const newValue = parseInt(inputs[0].value, 10) || 1;
			mmUpdatePrice(newValue);
		}
	});
});

/*********************************************************************************************
 * 📏 SIZE GATE — pas de taille présélectionnée à l'arrivée sur la fiche
 * Bloque l'ajout panier (form principal + sticky mobile) tant que la taille n'est pas choisie,
 * affiche le message sous les tailles et y fait défiler. Écoute en capture sur document :
 * passe avant le listener submit que mm_cart.js pose directement sur le form.
 */
document.addEventListener('submit', (e) => {
	const form = e.target;
	if (!form?.matches?.('#product-form, #product-form-sticky')) return;
	const selector = document.querySelector('variant-selector');
	if (!selector?.hasAllOptionsSelected || selector.hasAllOptionsSelected()) return;

	e.preventDefault();
	e.stopImmediatePropagation();
	const group = selector.toggleSizeError(true);
	if (!group) return;
	// Desktop : le scroll est piloté par Lenis (scrollIntoView natif serait écrasé) ; mobile : natif.
	if (window.LenisManager?.instance && !window.isMobile()) {
		window.LenisManager.scrollTo(group, { offset: -window.innerHeight / 3 });
	} else {
		group.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}
}, true);

/*********************************************************************************************
 * 📌 STICKY CTA — mobile uniquement
 */
const StickyCTAManager = {
	init() {
		const sticky = document.querySelector('.mm-pdp-sticky');
		if (!sticky || !document.querySelector('.mm-product-cta') || !window.isMobile()) return;

		sticky.setAttribute('aria-hidden', 'true');

		const observer = new IntersectionObserver(([entry]) => {
			/* Ignorer le callback d'un ancien CTA retiré du DOM lors d'un reload variante :
			   un nœud détaché rapporte isIntersecting:false → ferait clignoter le sticky en
			   --visible le temps que le nouveau CTA soit observé. */
			if (!entry.target.isConnected) return;
			const visible = !entry.isIntersecting;
			sticky.classList.toggle('mm-pdp-sticky--visible', visible);
			sticky.setAttribute('aria-hidden', String(!visible));
		}, { threshold: 0 });

		/* Le CTA classique vit dans [data-product-reload] : il est remplacé à chaque
		   changement de variante. On (ré)observe le nœud courant à l'init et après
		   chaque reload, sinon l'observer reste accroché à l'ancien nœud détaché. */
		let observedCTA = null;
		const observeCTA = () => {
			const cta = document.querySelector('.mm-product-cta');
			if (!cta || cta === observedCTA) return;
			if (observedCTA) observer.unobserve(observedCTA);
			observer.observe(cta);
			observedCTA = cta;
		};
		observeCTA();
		document.addEventListener('product:product-reload-complete', observeCTA);

		/* Sync variant depuis le form principal */
		document.addEventListener('product:variant-found', (e) => {
			const { variantId, available, price, compare_at_price } = e.detail || {};
			if (!variantId) return;

			const input = sticky.querySelector('[data-sticky-variant-id]');
			if (input) input.value = variantId;

			const btn = sticky.querySelector('.mm-pdp-sticky__atc');
			if (!btn) return;

			if (!available) {
				/* Switch vers état indisponible (CTA désactivé) */
				btn.type = 'button';
				btn.removeAttribute('name');
				btn.disabled = true;
				btn.setAttribute('aria-disabled', 'true');
				btn.classList.add('mm-pdp-sticky__atc--sold-out');
				const label = btn.querySelector('.mm-pdp-sticky__atc-label');
				if (label) label.textContent = window.mmI18n?.sold_out || 'Indisponible';
				/* Masquer le groupe prix */
				const priceGroup = btn.querySelector('.mm-pdp-sticky__atc-price-group');
				if (priceGroup) priceGroup.style.display = 'none';
			} else {
				/* Switch vers état add-to-cart */
				btn.type = 'submit';
				btn.name = 'add';
				btn.disabled = false;
				btn.removeAttribute('aria-disabled');
				btn.classList.remove('mm-pdp-sticky__atc--sold-out');
				btn.dataset.variantId = variantId;
				const label = btn.querySelector('.mm-pdp-sticky__atc-label');
				if (label) label.textContent = window.mmI18n?.add_to_cart || 'Ajouter au panier';

				const priceEl = btn.querySelector('[data-sticky-price]');
				let priceGroup = btn.querySelector('.mm-pdp-sticky__atc-price-group');

				/* Recréer le groupe prix s'il a été masqué */
				if (priceGroup) {
					priceGroup.style.display = '';
				} else {
					priceGroup = document.createElement('span');
					priceGroup.className = 'mm-pdp-sticky__atc-price-group';
					const priceSpan = document.createElement('span');
					priceSpan.className = 'mm-pdp-sticky__atc-price';
					priceSpan.dataset.stickyPrice = '';
					priceGroup.appendChild(priceSpan);
					btn.appendChild(priceGroup);
				}

				const priceElFinal = btn.querySelector('[data-sticky-price]');
				const compareEl = btn.querySelector('[data-sticky-compare-price]');

				if (priceElFinal && price) {
					priceElFinal.textContent = window.formatMoney(price);
				}

				if (compare_at_price && compare_at_price > price) {
					const formattedCompare = window.formatMoney(compare_at_price);

					if (compareEl) {
						compareEl.textContent = formattedCompare;
						compareEl.style.display = '';
					} else if (priceElFinal) {
						const newCompare = document.createElement('span');
						newCompare.className = 'mm-pdp-sticky__atc-compare-price';
						newCompare.dataset.stickyComparePrice = '';
						newCompare.textContent = formattedCompare;
						priceElFinal.parentElement.insertBefore(newCompare, priceElFinal);
					}
				} else if (compareEl) {
					compareEl.style.display = 'none';
				}
			}
		});
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => StickyCTAManager.init());
} else {
	StickyCTAManager.init();
}


/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	// Product events
	['initializing', 'initialized', 'variant-change-start', 'variant-found', 'variant-not-found',
		'quantity-update-start', 'quantity-update-success', 'quantity-update-error'].forEach(event => {
		document.addEventListener(`product:${event}`, e =>
			console.log(`🛍️ [PRODUCT] ${event}:`, e.detail)
		);
	});

}
